import IEntityActions, { IOverrideEntityActions } from '../API/Interfaces/IEntityActions';
import MaterialTable, { Column, MaterialTableProps, Query, QueryResult, MTableToolbar } from 'material-table';
import React, { ReactElement, useMemo, useState } from 'react';

import { AddIcon } from './Components/Icons';
import IResponseBaseTDto from '../API/Interfaces/IResponseBaseTDto';
import ISearchBaseDto from '../API/Interfaces/ISearchBaseDto';
import { mergeDeep, adjustTable } from '../Constants/constants';
import { tableIcons } from '../Utils/tableIcons';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { Typography, Tooltip, Box, FormControl, FormControlLabel, Checkbox, makeStyles } from '@material-ui/core';
import { CSSProperties } from '@material-ui/core/styles/withStyles';

export interface IEntityPageAdditionalAction {
	title: string;
	onClick: () => void;
	isFreeAction: boolean;
	tooltip: string;
}

export interface IEntityPageAdditionalFilter {
	title: string;
	onClick: () => void;
	isFreeAction: boolean;
	checked: boolean;
}

export interface IEntityPageProps<T extends object, U extends object> {
	form?: ReactElement;
	EntityActions?: IEntityActions<T, U> | IOverrideEntityActions<T, U>;
	headers: Array<Column<any>>;
	modalEntityName?: string;
	tableProps?: MaterialTableProps<any>;
	tableRef: any;
	//entityDetailsProps?: IEntityDetailsProps;
	pageTitle?: string;
	defaultPerPage?: number;
	disableAddNew?: boolean;
	isButtonDisabled?: boolean;
	isDetails?: boolean;
	additionalAction?: IEntityPageAdditionalAction;
	additionalFilter?: IEntityPageAdditionalFilter;
	disableSearchBar?: boolean;
	addDefaultAction?: boolean;
	customFilters?: JSX.Element;
	align?: boolean;
	disabledTooltip?: string;
	titleElement?: any;
	emptyDataSourceMessage?: string;
}

export interface EntityObject extends Object {
	id: string;
}

const useStyles = makeStyles((theme) => ({
	defaultAction: {
		[theme.breakpoints.down(500)]: {
			width: '18px',
			height: '18px',
		},
	},
	emptyMessage: {
		maxWidth: '380px',
		padding: '30px',
		marginLeft: 'auto',
		marginRight: 'auto',
	},
}));

// T detailsDto
// U   basicDto
export default function EntityPage<T extends EntityObject, U extends Object>(props: IEntityPageProps<any, any>) {
	const { t } = useTranslation();
	const {
		tableRef,
		defaultPerPage,
		pageTitle,
		EntityActions: incomingEntityActions,
		headers,
		tableProps,
		disableAddNew,
		isDetails,
		additionalAction,
		additionalFilter,
		disableSearchBar = false,
		addDefaultAction = true,
		customFilters,
		align,
		isButtonDisabled,
		disabledTooltip,
		titleElement,
		emptyDataSourceMessage,
	} = props;
	const { enqueueSnackbar } = useSnackbar();
	const classes = useStyles();
	const [perPage, setPerPage] = useState(defaultPerPage || 10);
	const errorDialog = (message?: string) =>
		enqueueSnackbar(`${t('common.errorOccured')}${message ? ': ' : ''}${message && message}`, { variant: 'error' });

	let EntityActions: IEntityActions<T, U> = {
		api: {},
		setModalOpen: () => {},
		fetchEntity: async function (id: string) {
			try {
				const response: IResponseBaseTDto<T> = await EntityActions.api.GetOne(id);
				if (response.success) return response.data;
			} catch (e) {
				errorDialog(e.message);
			}
			return {} as T;
		},
		getTableData: async function (query: Query<U>) {
			const result: QueryResult<U> = {
				data: [],
				page: 0,
				totalCount: 0,
			};
			const request: ISearchBaseDto = {
				itemsPerPage: query.pageSize,
				pageNumber: query.page ? query.page + 1 : 1,
				searchPhrase: query.search,
				sortColumn: '',
				sortDirection: '',
			};

			if (query.orderBy) request.sortColumn = query.orderBy.field as string;

			if (query.orderDirection) request.sortDirection = query.orderDirection;

			try {
				const response = await EntityActions.api.GetPage(request);

				if (response.success) {
					result.data = response.data.items;
					result.page = response.data.currentPage - 1;
					result.totalCount = response.data.totalItemsCount;
					return result;
				} else {
					let message = t(`errorCodes.${response.errors[0].errorCode}`);
					enqueueSnackbar(`${t('common.errorOccured')}: ${message}`, {
						variant: 'error',
					});
				}
			} catch (e) {
				errorDialog(e.message);
			}
			return result;
		},
	};
	if (incomingEntityActions) EntityActions = mergeDeep(EntityActions, incomingEntityActions);

	const addCaseInsensitiveSorting = (data: any): any => {
		Object.keys(data).forEach(function (item) {
			let shouldBeSorted = !data[item].customSort && data[item].field && !data[item].hidden;

			// overrides material-table sorting mechanism witch is case sensitive
			if (shouldBeSorted)
				data[item].customSort = (a: any, b: any) => {
					try {
						return a[data[item].field]?.toLowerCase().localeCompare(b[data[item].field]?.toLowerCase());
					} catch (e) {
						return 0;
					}
				};
		});
		return data;
	};

	const addActions = () => {
		if (disableAddNew) return [];

		const defaultAction = {
			icon: () => <AddIcon className={classes.defaultAction} isSelected={true} />,
			tooltip: isButtonDisabled && disabledTooltip ? disabledTooltip : t('table.body.addTooltip'),
			isFreeAction: true,
			onClick: () => EntityActions.setModalOpen(true),
			title: '',
			disabled: isButtonDisabled,
		};

		let actions: any[] = [];
		if (addDefaultAction !== false && defaultAction) actions.push(defaultAction);

		if (additionalAction) {
			actions.push({
				icon: () => <></>,
				tooltip: additionalAction.tooltip,
				isFreeAction: additionalAction.isFreeAction,
				onClick: additionalAction.onClick,
				title: additionalAction.title,
			});
		}

		if (additionalFilter) {
			actions.push({
				icon: () => (
					<FormControl style={{ marginLeft: '15px' }}>
						<FormControlLabel
							control={<Checkbox color='secondary' checked={additionalFilter.checked} onChange={additionalFilter.onClick} />}
							label={additionalFilter.title}
						/>
					</FormControl>
				),
				isFreeAction: additionalFilter.isFreeAction,
				onClick: () => undefined,
				title: additionalFilter.title,
			});
		}

		return actions;
	};

	const getTableLocalization = () => {
		const localization = t('table', { returnObjects: true }) as any;
		localization.toolbar.searchPlaceholder = t('table.toolbar.searchPlaceholder');
		if (emptyDataSourceMessage)
			localization.body.emptyDataSourceMessage = <div className={classes.emptyMessage}>{emptyDataSourceMessage}</div>;

		return localization;
	};

	let commonTableProps: MaterialTableProps<any> = {
		onChangeRowsPerPage: (pagesize: number) => setPerPage(pagesize),
		tableRef: tableRef,
		icons: tableIcons,
		title: titleElement ? titleElement : pageTitle,
		columns: adjustTable(addCaseInsensitiveSorting(headers), isDetails), //, isMobile, defaultMaxContent, firstColumn),
		data: (query: any) => EntityActions.getTableData(query),
		localization: getTableLocalization(),
		options: {
			defaultExpanded: true,
			actionsColumnIndex: 99,
			padding: 'dense',
			emptyRowsWhenPaging: false,
			draggable: false,
			pageSize: perPage,
			toolbarButtonAlignment: 'left',
			search: !disableSearchBar,
			debounceInterval: 500,
		},
		actions: addActions(),
	};
	if (tableProps) commonTableProps = mergeDeep(commonTableProps, tableProps);
	let groupedTable = false;

	commonTableProps.columns.map((c) => {
		if (c.grouping === true) groupedTable = true;

		return null;
	});

	const getAction = (props: any) => {
		const { title, tooltip, onClick } = props.action;
		const titleStyle = {
			display: 'inline-block',
			marginTop: '14px',
			fontSize: '1.12rem',
			visibility: title ? 'visible' : 'hidden',
		} as CSSProperties;
		const margin = addActions().length === 1 ? { marginLeft: '10px' } : {};
		return (
			<>
				<div style={{ ...margin, display: 'inline-flex', marginBottom: '6px' }}>
					<Typography variant='h6' className='MuiTypography-colorPrimary' style={titleStyle}>
						{title ? title : '.'}
					</Typography>
					<div
						className='MuiButtonBase-root MuiIconButton-root MuiIconButton-colorInherit'
						style={{ fontSize: '1.12rem' }}
						onClick={onClick}
					>
						<Tooltip title={tooltip}>
							<Box style={{ cursor: 'pointer', width: '24px', height: '24px' }}>
								<AddIcon isSelected={true} style={{ fontSize: '1.12rem', marginTop: '4px' }} />
							</Box>
						</Tooltip>
					</div>
				</div>
			</>
		);
	};

	const getCommonTableProps = () => {
		if (customFilters) {
			commonTableProps.components = {
				Toolbar: (props) => (
					<div>
						<MTableToolbar {...props} />
						<div style={{ paddingLeft: !align ? '18px' : '8px' }}>{customFilters}</div>
					</div>
				),
			};
		}

		if (!additionalAction) return commonTableProps;

		commonTableProps.components = {
			Action: getAction,
		};

		return commonTableProps;
	};

	const table = useMemo(
		() => (
			<>
				<MaterialTable {...getCommonTableProps()} />
				<Box mt={5}></Box>
			</>
		),

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[customFilters]
	);

	return <div className={groupedTable ? 'GroupedTable' : ''}>{customFilters ? table : <MaterialTable {...getCommonTableProps()} />}</div>;
}

EntityPage.defaultProps = {
	isDetails: false,
};
